import { useState, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import {
  Upload, Music, Image as ImageIcon, Loader2, X, Check,
  Disc3, Clock, Hash, Zap, Tag
} from 'lucide-react';

const GENRES = [
  'Hip-Hop', 'R&B', 'Trap', 'Pop', 'Afrobeats', 'Go-Go',
  'Drill', 'Lo-Fi', 'Soul', 'Dance', 'Electronic', 'Rock',
  'Baltimore Club', 'Boom Bap', 'Jersey Club', 'Reggaeton'
];

const MOODS = [
  'Dark', 'Chill', 'Hype', 'Sad', 'Upbeat', 'Aggressive',
  'Melodic', 'Bouncy', 'Smooth', 'Hard', 'Ambient', 'Party'
];

const KEYS = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
  'Cm', 'C#m', 'Dm', 'D#m', 'Em', 'Fm', 'F#m', 'Gm', 'G#m', 'Am', 'A#m', 'Bm'
];

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function BeatUploadForm({ onClose, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1=files, 2=details
  const [uploading, setUploading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [coverArtUrl, setCoverArtUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [bpm, setBpm] = useState('');
  const [musicalKey, setMusicalKey] = useState('');
  const [mood, setMood] = useState('');
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [isFree, setIsFree] = useState(true);

  const handleAudioSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select an MP3 or WAV file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Audio file must be under 50MB');
      return;
    }
    setAudioFile(file);
    // Auto-detect title from filename
    if (!title) {
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setTitle(name);
    }
    // Get duration from audio element
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setDuration(Math.round(audio.duration));
      URL.revokeObjectURL(url);
    });
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const uploadFiles = async () => {
    if (!audioFile) {
      toast.error('Please select an audio file');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const headers = getAuthHeaders();

    try {
      // Upload audio
      setUploadStatus('Uploading audio...');
      const audioFormData = new FormData();
      audioFormData.append('file', audioFile);
      const audioRes = await fetch(`${API}/beats/upload-audio`, {
        method: 'POST',
        headers: { 'Authorization': headers.Authorization },
        body: audioFormData
      });
      if (!audioRes.ok) {
        const err = await audioRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Audio upload failed');
      }
      const audioData = await audioRes.json();
      setAudioUrl(audioData.audio_url);
      if (audioData.duration) setDuration(audioData.duration);
      setUploadProgress(60);

      // Upload cover if provided
      if (coverFile) {
        setUploadStatus('Uploading cover art...');
        const coverFormData = new FormData();
        coverFormData.append('file', coverFile);
        const coverRes = await fetch(`${API}/beats/upload-cover`, {
          method: 'POST',
          headers: { 'Authorization': headers.Authorization },
          body: coverFormData
        });
        if (coverRes.ok) {
          const coverData = await coverRes.json();
          setCoverArtUrl(coverData.cover_art_url);
        }
      }
      setUploadProgress(100);
      setUploadStatus('Files uploaded!');
      setStep(2);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
      setUploadStatus('');
    } finally {
      setUploading(false);
    }
  };

  const createBeat = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!audioUrl) {
      toast.error('Audio file is required');
      return;
    }
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const beatData = {
        title: title.trim(),
        audio_url: audioUrl,
        cover_art_url: coverArtUrl || null,
        duration,
        genre,
        bpm: bpm ? parseInt(bpm) : null,
        key: musicalKey,
        mood,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        description: description.trim(),
        price: isFree ? null : (price ? parseFloat(price) : null),
        is_free: isFree
      };

      const res = await fetch(`${API}/beats`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(beatData)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create beat');
      }

      const data = await res.json();
      toast.success('Beat uploaded successfully!');
      onSuccess?.(data.beat);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed to create beat');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card
        className="w-full max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="beat-upload-form"
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Upload className="w-4 h-4 text-orange-400" />
              </div>
              <h2 className="text-lg font-bold text-foreground">Upload Beat</h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-upload-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`flex-1 h-1 rounded-full ${step >= 1 ? 'bg-orange-500' : 'bg-muted'}`} />
            <div className={`flex-1 h-1 rounded-full ${step >= 2 ? 'bg-orange-500' : 'bg-muted'}`} />
          </div>

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Audio File */}
              <div>
                <Label className="text-sm text-foreground/80 mb-2 block">Audio File *</Label>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,.mp3,.wav"
                  onChange={handleAudioSelect}
                  className="hidden"
                  data-testid="audio-file-input"
                />
                {audioFile ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <Music className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{audioFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(audioFile.size / (1024 * 1024)).toFixed(1)}MB
                        {duration > 0 && ` · ${formatDuration(duration)}`}
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={() => { setAudioFile(null); setDuration(0); }}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => audioInputRef.current?.click()}
                    className="w-full p-6 rounded-lg border-2 border-dashed border-border hover:border-orange-500/40 transition-colors text-center"
                    data-testid="select-audio-btn"
                  >
                    <Music className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select audio file</p>
                    <p className="text-xs text-muted-foreground/80 mt-1">MP3 or WAV, up to 50MB</p>
                  </button>
                )}
              </div>

              {/* Cover Art */}
              <div>
                <Label className="text-sm text-foreground/80 mb-2 block">Cover Art (optional)</Label>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  onChange={handleCoverSelect}
                  className="hidden"
                  data-testid="cover-file-input"
                />
                <div className="flex gap-3">
                  {coverPreview ? (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                      <button
                        onClick={() => { setCoverFile(null); setCoverPreview(null); }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-background/70 flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => coverInputRef.current?.click()}
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-orange-500/40 transition-colors flex flex-col items-center justify-center flex-shrink-0"
                      data-testid="select-cover-btn"
                    >
                      <ImageIcon className="w-5 h-5 text-muted-foreground mb-1" />
                      <span className="text-[10px] text-muted-foreground/80">Add Art</span>
                    </button>
                  )}
                  <div className="flex-1 text-xs text-muted-foreground pt-2">
                    <p>Recommended: Square image, 1000x1000px</p>
                    <p className="mt-1">JPEG, PNG, or WebP</p>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground text-center">{uploadStatus}</p>
                </div>
              )}

              <Button
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                onClick={uploadFiles}
                disabled={!audioFile || uploading}
                data-testid="upload-files-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload Files</>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Beat Details */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/20 mb-2">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400">Files uploaded successfully</span>
              </div>

              {/* Title */}
              <div>
                <Label className="text-sm text-foreground/80 mb-1 block">Title *</Label>
                <Input
                  data-testid="beat-title-input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Beat name"
                  className="bg-muted/50 border-border"
                />
              </div>

              {/* Genre & BPM */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-foreground/80 mb-1 block">Genre</Label>
                  <select
                    data-testid="beat-genre-select"
                    value={genre}
                    onChange={e => setGenre(e.target.value)}
                    className="w-full h-9 rounded-md bg-muted/50 border border-border text-sm text-foreground px-3"
                  >
                    <option value="">Select genre</option>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-foreground/80 mb-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" />BPM
                  </Label>
                  <Input
                    data-testid="beat-bpm-input"
                    type="number"
                    value={bpm}
                    onChange={e => setBpm(e.target.value)}
                    placeholder="140"
                    className="bg-muted/50 border-border"
                  />
                </div>
              </div>

              {/* Key & Mood */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-foreground/80 mb-1 flex items-center gap-1">
                    <Hash className="w-3 h-3" />Key
                  </Label>
                  <select
                    data-testid="beat-key-select"
                    value={musicalKey}
                    onChange={e => setMusicalKey(e.target.value)}
                    className="w-full h-9 rounded-md bg-muted/50 border border-border text-sm text-foreground px-3"
                  >
                    <option value="">Select key</option>
                    {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-foreground/80 mb-1 block">Mood</Label>
                  <select
                    data-testid="beat-mood-select"
                    value={mood}
                    onChange={e => setMood(e.target.value)}
                    className="w-full h-9 rounded-md bg-muted/50 border border-border text-sm text-foreground px-3"
                  >
                    <option value="">Select mood</option>
                    {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label className="text-sm text-foreground/80 mb-1 flex items-center gap-1">
                  <Tag className="w-3 h-3" />Tags
                </Label>
                <Input
                  data-testid="beat-tags-input"
                  value={tags}
                  onChange={e => setTags(e.target.value)}
                  placeholder="trap, dark, 808s (comma separated)"
                  className="bg-muted/50 border-border"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm text-foreground/80 mb-1 block">Description</Label>
                <Textarea
                  data-testid="beat-description-input"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe your beat..."
                  rows={2}
                  className="bg-muted/50 border-border"
                />
              </div>

              {/* Duration display */}
              {duration > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Duration: {formatDuration(duration)}</span>
                </div>
              )}

              <Button
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                onClick={createBeat}
                disabled={!title.trim() || uploading}
                data-testid="create-beat-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Disc3 className="w-4 h-4 mr-2" />Publish Beat</>
                )}
              </Button>

              <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => setStep(1)}>
                Back to Files
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
